import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HeaderFooterLayout from '../components/header-footer-layout';
import Header from '../components/header';
import StepProgress from '../components/step-progress';
import { scrollToTop } from '../utils/helpers';
import { createEmptyBooking } from '../models/repair-bookings';
import ProductSelection from '../pages/product-selection';
import PurposeSelection from '../pages/purpose-selection';
import ShopSelection from '../pages/shop-selection';
import DateSelection from '../pages/date-selection';
import TimeSelection from '../pages/time-selection';
import PersonalInformationEntry from '../pages/personal-information-entry';
import FinalConfirmation from '../pages/final-confirmation';
import InvalidNavigation from '../pages/invalid-navigation';
import useBookingState from '../hooks/use-booking-state';
import useBookingCreatePages from '../hooks/use-booking-create-pages';
import useCustomerOptions from '../hooks/use-customer-options';
import ConsoleLogoutPopup from '../pages/console-logout-popup';
import ConsoleCompletion from '../pages/console-completion';
import LoadingFallback from '../components/loading-fallback';
import { createConsoleBooking, fetchTimeSlots } from '../utils/api';
import { consoleLogout } from '../utils/paths';

const BookingConsoleCreateFlow = () => {
    const navigate = useNavigate();
    const { booking, actions } = useBookingState(createEmptyBooking);
    const csrfToken = window.preloadedState.environment.csrfToken;
    const {
        currentPage,
        pageConfig,
        pushPageStack,
        popPageStack,
        complete,
        clearPageStack,
        steps,
        session,
    } = useBookingCreatePages('console', csrfToken);
    const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);

    const { lineStatuses, error } = useCustomerOptions(csrfToken);

    useEffect(() => {
        scrollToTop();
    }, [currentPage]);

    useEffect(() => {
        if (error) {
            alert(error.message);
        }
    }, [error]);

    const resetFlow = () => {
        actions.reset();
        clearPageStack();
    };

    return (
        <HeaderFooterLayout
            title={`【 予約管理 】${currentPage.status === 'complete' ? '完了' : '来店予約'}`}
            onHome={() => {
                navigate('/console/bookings/');
            }}
            onLogout={() => {
                setShowLogoutConfirmation(true);
            }}
            withFooter={false}
        >
            {pageConfig.isShopSelected && <Header title={booking.shop?.name ?? ''} />}
            {pageConfig.stepIndex !== null && (
                <StepProgress steps={steps} currentStep={pageConfig.stepIndex} />
            )}
            {currentPage.status === 'invalid' ? (
                <InvalidNavigation onClose={resetFlow} />
            ) : !session ? (
                <LoadingFallback />
            ) : currentPage.status === 'product-selection' ? (
                <ProductSelection
                    session={session}
                    groupKey={currentPage.groupKey}
                    onNext={(groupKey) => {
                        pushPageStack({ status: 'product-selection', groupKey });
                    }}
                    onComplete={(product) => {
                        actions.setProduct(product);
                        pushPageStack({ status: 'purpose-selection' });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'purpose-selection' ? (
                <PurposeSelection
                    session={session}
                    productId={booking.product!.id}
                    selectedServices={booking.repairOptions}
                    toggleService={actions.toggleRepairOption}
                    onComplete={() => {
                        pushPageStack({ status: 'shop-selection' });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'shop-selection' ? (
                <ShopSelection
                    session={session}
                    productId={booking.product!.id}
                    selectedShop={booking.shop}
                    onComplete={(selectedShop) => {
                        actions.setShop(selectedShop);
                        pushPageStack({ status: 'date-selection' });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'date-selection' ? (
                <DateSelection
                    selectedSlot={booking.selectedSlot}
                    fetchTimeSlots={() =>
                        fetchTimeSlots(
                            session.csrfToken,
                            booking.shop!.shop_id,
                            session.sessionId,
                            undefined,
                            booking.product!.id,
                            booking.repairOptions.map((s) => s.id)
                        )
                    }
                    onComplete={(date, slots) => {
                        pushPageStack({
                            status: 'time-selection',
                            selectedDate: date,
                            slots: slots,
                        });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'time-selection' ? (
                <TimeSelection
                    selectedDate={currentPage.selectedDate}
                    slots={currentPage.slots}
                    onComplete={(slot) => {
                        actions.setSelectedSlot(slot);
                        pushPageStack({ status: 'personal-info' });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'personal-info' ? (
                <PersonalInformationEntry
                    info={booking.personalInfo}
                    personalInfoConfig={{ granularity: 'extended', lineStatuses }}
                    handleChange={actions.changePersonalInfo}
                    onComplete={() => {
                        pushPageStack({ status: 'confirm' });
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'confirm' ? (
                <FinalConfirmation
                    personalInfoConfig={{ granularity: 'extended', lineStatuses }}
                    booking={booking}
                    onSubmit={async () => {
                        const result = await createConsoleBooking(
                            session.csrfToken,
                            session.sessionId,
                            booking
                        );
                        complete(result.confirmation_id);
                    }}
                    onCancel={popPageStack}
                />
            ) : currentPage.status === 'complete' ? (
                <ConsoleCompletion
                    confirmationId={currentPage.confirmationId}
                    onClose={() => {
                        navigate('/console/bookings/');
                    }}
                    onLogout={consoleLogout}
                />
            ) : null}
            {showLogoutConfirmation && (
                <ConsoleLogoutPopup
                    onOk={() => {
                        consoleLogout();
                        setShowLogoutConfirmation(false);
                    }}
                    onCancel={() => {
                        setShowLogoutConfirmation(false);
                    }}
                />
            )}
        </HeaderFooterLayout>
    );
};

export default BookingConsoleCreateFlow;
